/**
 * Composable for managing critical resource loading
 * Prioritizes above-the-fold content loading
 */
export function useCriticalResources() {
  const criticalImagesLoaded = ref<Set<string>>(new Set())
  const isCriticalContentReady = ref(false)

  /**
   * Preload a critical image with high priority
   */
  function preloadCriticalImage(src: string, priority: 'high' | 'low' = 'high'): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image()

      // Set loading priority
      if (priority === 'high') {
        img.setAttribute('fetchpriority', 'high')
      }

      img.onload = () => {
        criticalImagesLoaded.value.add(src)
        resolve()
      }

      img.onerror = (error) => {
        console.warn(`Failed to load critical image: ${src}`, error)
        reject(error)
      }

      img.src = src
    })
  }

  /**
   * Preload multiple critical images
   */
  async function preloadCriticalImages(imageSources: string[]): Promise<void> {
    const promises = imageSources.map(src => preloadCriticalImage(src, 'high'))

    try {
      await Promise.all(promises)
      isCriticalContentReady.value = true
    } catch (error) {
      console.warn('Some critical images failed to load:', error)
      // Still mark as ready to avoid blocking the UI
      isCriticalContentReady.value = true
    }
  }

  /**
   * Check if a specific image is loaded
   */
  function isImageLoaded(src: string): boolean {
    return criticalImagesLoaded.value.has(src)
  }

  return {
    criticalImagesLoaded: readonly(criticalImagesLoaded),
    isCriticalContentReady: readonly(isCriticalContentReady),
    preloadCriticalImage,
    preloadCriticalImages,
    isImageLoaded
  }
}
